import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Loader2 } from "lucide-react";
import { ChangelogInput } from "@/lib/api/changelog";
import { ChangesListEditor } from "./changes-list-editor";
import { RoleMultiselect } from "./role-multiselect";

const schema = z.object({
  title: z.string().min(1, "Title is required").max(120),
  subtitle: z.string().max(200).optional(),
  date_label: z
    .string()
    .max(40, "Keep it short — e.g. 'Jan 2026'")
    .optional(),
  body: z.string().min(1, "Body is required"),
  changes: z.array(z.string().min(1, "Change cannot be empty")),
  loom_url: z
    .string()
    .url("Must be a valid URL")
    .optional()
    .or(z.literal("")),
  role_ids: z
    .array(z.string())
    .min(1, "Pick at least one role to target"),
});

type FormValues = z.infer<typeof schema>;

interface ChangelogFormProps {
  defaultValues?: Partial<FormValues>;
  submitLabel: string;
  isSubmitting: boolean;
  onSubmit: (values: ChangelogInput) => void | Promise<void>;
  onCancel: () => void;
}

export function ChangelogForm({
  defaultValues,
  submitLabel,
  isSubmitting,
  onSubmit,
  onCancel,
}: ChangelogFormProps) {
  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      title: defaultValues?.title ?? "",
      subtitle: defaultValues?.subtitle ?? "",
      date_label: defaultValues?.date_label ?? "",
      body: defaultValues?.body ?? "",
      changes: defaultValues?.changes ?? [],
      loom_url: defaultValues?.loom_url ?? "",
      role_ids: defaultValues?.role_ids ?? [],
    },
  });

  const [bodyTab, setBodyTab] = useState("write");

  const handleSubmit = async (values: FormValues) => {
    await onSubmit({
      title: values.title,
      subtitle: values.subtitle || undefined,
      date_label: values.date_label || undefined,
      body: values.body,
      changes: values.changes.filter((c) => c.trim().length > 0),
      loom_url: values.loom_url || undefined,
      role_ids: values.role_ids,
    });
  };

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(handleSubmit)}
        className="space-y-4"
      >
        <FormField
          control={form.control}
          name="title"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Title</FormLabel>
              <FormControl>
                <Input placeholder="Shared Notes" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="subtitle"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Subtitle (optional)</FormLabel>
              <FormControl>
                <Input
                  placeholder="First iteration of the Hub (sharing notes)"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="date_label"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Date label (optional)</FormLabel>
              <FormControl>
                <Input placeholder="Jan 2026" {...field} />
              </FormControl>
              <FormDescription>
                Override the auto-derived month/year above the title. Leave
                blank to use the publish date.
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="body"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Body</FormLabel>
              <Tabs value={bodyTab} onValueChange={setBodyTab}>
                <TabsList>
                  <TabsTrigger value="write">Write</TabsTrigger>
                  <TabsTrigger value="preview">Preview</TabsTrigger>
                </TabsList>
                <TabsContent value="write">
                  <FormControl>
                    <Textarea
                      rows={8}
                      placeholder="Markdown supported. Describe the update and any new feature bullets."
                      {...field}
                    />
                  </FormControl>
                </TabsContent>
                <TabsContent value="preview">
                  <div className="prose prose-sm min-h-[200px] max-w-none rounded-md border bg-muted/40 p-4">
                    {field.value ? (
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>
                        {field.value}
                      </ReactMarkdown>
                    ) : (
                      <p className="text-sm text-muted-foreground">
                        Nothing to preview yet.
                      </p>
                    )}
                  </div>
                </TabsContent>
              </Tabs>
              <FormDescription>
                Markdown supported (headings, lists, bold, links).
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="changes"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Changes</FormLabel>
              <FormDescription>
                Renders as a &quot;Changes:&quot; bullet list under the body.
              </FormDescription>
              <FormControl>
                <ChangesListEditor
                  value={field.value}
                  onChange={field.onChange}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="loom_url"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Loom URL (optional)</FormLabel>
              <FormControl>
                <Input
                  type="url"
                  placeholder="https://www.loom.com/share/…"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="role_ids"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Targeted roles</FormLabel>
              <FormDescription>
                Only users with one of these roles will see the update.
              </FormDescription>
              <FormControl>
                <RoleMultiselect
                  value={field.value}
                  onChange={field.onChange}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="flex justify-end gap-2 pt-2">
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting && (
              <Loader2 className="mr-2 size-4 animate-spin" />
            )}
            {submitLabel}
          </Button>
        </div>
      </form>
    </Form>
  );
}
